/**
 * PrintFriendly — Reader Mode, PDF Editor, Save as PDF & Screenshot
 *
 * Exposes a large static WAR group (~400 files after glob expansion) to every origin
 * with no `use_dynamic_url` — including `assets/logo.png`, `print-in-tab.html` and
 * `assets/pf-styles.css`, all reachable from any page at a fixed URL.
 *
 * It also declares a *second* WAR entry with `use_dynamic_url: true`, scoped to search
 * result pages (Google/Bing), holding `search-widget.html`. We probe that as a control:
 * it stays unreachable even when the extension is installed — both because its URL
 * rotates per session and because it is scoped away from an arbitrary origin.
 *
 * Manifest verified against version 7.0.5.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ohlencieiipommannpdfcmfdpjjmeolj';

export const printfriendly: ExtensionDefinition = {
  id: ID,
  name: 'PrintFriendly: Reader Mode, PDF Editor, Save as PDF & Screenshot',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 1_000_000,
  notes:
    'Hundreds of static WAR on *://*/*, plus one use_dynamic_url resource scoped to ' +
    'search pages used here as a control. Detection is conclusive and CORS-free.',
  signals: [
    {
      id: 'war-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/logo.png via <img> — exposed to every origin and readable without ' +
        'a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'assets/logo.png'),
    },
    {
      id: 'war-print-in-tab-html',
      kind: 'web-accessible-resource',
      description: 'Fetch print-in-tab.html — a static entry point exposed to <all_urls>.',
      weight: 0.5,
      run: () => probeFetch(ID, 'print-in-tab.html'),
    },
    {
      id: 'war-pf-styles-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch assets/pf-styles.css — a vendor-named stylesheet, independent of the ' +
        'image and HTML paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'assets/pf-styles.css'),
    },
    {
      id: 'control-dynamic-search-widget',
      kind: 'web-accessible-resource',
      description:
        'CONTROL: fetch search-widget.html, declared use_dynamic_url and scoped to ' +
        'search-engine origins. It should fail even when installed — the URL rotates ' +
        'per session and the origin does not match — demonstrating the mitigation ' +
        'PrintFriendly applied to that one resource but not the other 400.',
      weight: 0,
      control: true,
      run: () => probeFetch(ID, 'search-widget.html'),
    },
  ],
};
